import { Button } from '@daily/shared/components/Button';
import { CaretDownIcon } from '@daily/shared/components/Icons/CaretDownIcon';
import { VisuallyHidden } from '@daily/shared/components/VisuallyHidden';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useMediaQuery } from '@daily/shared/hooks/useMediaQuery';
import { useResizeObserver } from '@daily/shared/hooks/useResizeObserver';
import classNames from 'classnames';
import deepEqual from 'fast-deep-equal';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { MAIN_ROOM_IDENTIFIER } from '/components/BreakoutRooms/helpers/constants';
import { useBreakoutSessionState } from '/components/BreakoutRooms/hooks/useBreakoutSessionState';
import { useChatMessages } from '/features/chat/contexts/ChatProvider';
import { hasReactions } from '/features/chat/util/advancedChat';
import { BROADCAST_CHAT_MESSAGE } from '/features/chat/util/constants';
import { useCallConfig } from '/hooks/useCallConfig';
import { usePreviousValue } from '/hooks/usePreviousValue';
import { isSafari } from '/lib/browserConfig';

import { ChatMessage } from './ChatMessage';

interface Props {
  loading: boolean;
  showBroadcastMessages: boolean;
}

const scrollBtnVisibleClass = 'visible';

export const ChatScrollView: React.FC<React.PropsWithChildren<Props>> = ({
  loading,
  showBroadcastMessages,
}) => {
  const { t } = useTranslation();
  const { colors, isDarkMode, mediaQueries, zIndex } = useTheme();
  const { enableAdvancedChat } = useCallConfig();
  const { markMessagesAsRead, messages } = useChatMessages();
  const chatRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const lastScrollTime = useRef(0);
  const isScrolledToBottom = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const [renderedMessages, setRenderedMessages] = useState<typeof messages>([]);
  const chatMessageHeights = useRef<Record<string, number>>({});
  const [updateHeightCount, setUpdateHeightCount] = useState(0);
  const [enableVirtualScrolling, setEnableVirtualScrolling] = useState(false);
  const scrollToBottomRef = useRef<HTMLButtonElement>(null);
  const isCoarse = useMediaQuery(mediaQueries.coarse);
  /**
   * Minimum height per chat message. Used to calculate minimum height of complete chat.
   */
  const MIN_MSG_HEIGHT = useMemo(() => (isCoarse ? 56 : 40), [isCoarse]);

  const { currentBreakoutRoom, hasJoinedBreakout } = useBreakoutSessionState();

  const filteredMessages = useMemo(
    () =>
      messages.filter(
        (m) =>
          (!hasJoinedBreakout && m.room === MAIN_ROOM_IDENTIFIER) ||
          (hasJoinedBreakout && m.room === currentBreakoutRoom.id) ||
          m.room === BROADCAST_CHAT_MESSAGE
      ),
    [currentBreakoutRoom.id, hasJoinedBreakout, messages]
  );

  // returns an array with all messages that have at least 1 reaction

  const filterMessagesWithReactions = (messages) =>
    messages.filter(hasReactions);
  /**
   * Update rendered messages
   */
  useLayoutEffect(() => {
    const scrollable = chatRef.current;
    if (!scrollable || loading) return;

    setRenderedMessages((rmsgs) => {
      // Check if amount of messages or message structure changed.
      if (
        filteredMessages.length > rmsgs.length ||
        rmsgs.some((m, i) => !deepEqual(m, filteredMessages?.[i]))
      ) {
        isScrolledToBottom.current =
          // Ignore subpixel rounding differences
          Math.abs(
            scrollable.scrollTop -
              (scrollable.scrollHeight - scrollable.clientHeight)
          ) < 2;
        return filteredMessages;
      }
      return rmsgs;
    });
  }, [filteredMessages, loading]);

  const previousRenderedMessages = usePreviousValue(renderedMessages);

  /**
   * Enable virtual scrolling when scroll container is actually scrollable.
   *
   * We don't enable virtual scrolling Safari 14:
   * There's a bug in Safari 14 where virtual scrolling breaks,
   * once the first message of the initial viewFrame is being removed.
   * When that happens the scroll container's clientHeight is briefly
   * set to its scrollHeight, which also resets the scrollTop to 0.
   * When that happens the viewFrame briefly updates to the next desired frame,
   * e.g. from [0, 33] to [1, 34], but immediately after to contain all messages,
   * e.g. [0, 55]. Due to the automatic reset of the scrollTop position, the user
   * is thrown back to the start of the chat.
   *
   * cst 2023-02-24:
   * I haven't found a way to work around this issue within our implementation
   * of virtual scrolling. I considered switching to @tanstack/react-virtual,
   * but the migration effort is relatively high and the last time I tried to
   * convert our chat to @tanstack/react-virtual, we ran into a couple of
   * undesirable issues. We might want to retry in the future, e.g. when
   * @tanstack/react-virtual@3.0.0 is out of beta.
   */
  useResizeObserver(
    innerRef,
    useCallback(() => {
      const scrollable = chatRef.current;
      if (!scrollable || loading || enableVirtualScrolling || isSafari(14, 14))
        return;
      setEnableVirtualScrolling((enabled) =>
        enabled ? enabled : scrollable.scrollHeight > scrollable.clientHeight
      );
    }, [enableVirtualScrolling, loading])
  );

  /**
   * Allows to update rendered heights for multiple messages.
   */
  const updateMessageHeights = useCallback(
    (updates: Record<string, number>) => {
      const oldHeights = { ...chatMessageHeights.current };
      const newHeights = { ...oldHeights, ...updates };
      chatMessageHeights.current = newHeights;
      if (!deepEqual(oldHeights, newHeights)) {
        setUpdateHeightCount((c) => c + 1);
      }
    },
    []
  );
  /**
   * Index based boundaries for virtual scrolling.
   */
  const [viewFrame, setViewFrame] = useState([
    Math.max(0, messages.length - 15),
    Math.max(0, messages.length - 1),
  ]);

  /**
   * Update relevant styles for virtual scrolling.
   */
  useEffect(() => {
    if (!enableVirtualScrolling) {
      innerRef.current.style.height = '';
      innerRef.current
        .querySelectorAll('[data-msg-id]')
        .forEach((msgEl: HTMLDivElement) => {
          msgEl.style.top = '';
        });
      return;
    }
    const totalHeight = renderedMessages.reduce(
      (sum, msg) =>
        sum + (chatMessageHeights.current?.[msg.id] ?? MIN_MSG_HEIGHT),
      0
    );
    innerRef.current.style.height = `${totalHeight}px`;

    const heightBefore = renderedMessages
      .slice(0, viewFrame[0])
      .reduce(
        (sum, msg) =>
          sum + (chatMessageHeights.current?.[msg.id] ?? MIN_MSG_HEIGHT),
        0
      );

    let top = 0;

    innerRef.current
      .querySelectorAll('[data-msg-id]')
      .forEach((msgEl: HTMLDivElement) => {
        const id = msgEl.getAttribute('data-msg-id');
        const height = chatMessageHeights.current?.[id] ?? MIN_MSG_HEIGHT;
        msgEl.style.top = `${heightBefore + top}px`;
        top += height;
      });
  }, [
    enableVirtualScrolling,
    MIN_MSG_HEIGHT,
    renderedMessages,
    updateHeightCount,
    viewFrame,
  ]);

  /**
   * Updates viewFrame for rendered messages, based on target's scroll position.
   */
  const updateScrollFrame = useCallback(
    (target: HTMLDivElement) => {
      const scrollTop = Math.ceil(target.scrollTop);
      const scrollBottom = scrollTop + target.clientHeight;
      let start: number;
      let end: number;
      let bottom = target.scrollHeight;
      if (enableVirtualScrolling) {
        /**
         * Chat is actually scrollable, so based on the scroll position
         * we'll have to find the messages of interest.
         * For that we'll loop through the messages and add each message's
         * height (or assumed fallback height) until we reach the actual
         * scroll frame visible to the user.
         * This way we can find the first and last visible messages at the
         * top and bottom of the scroll frame.
         */
        for (let i = renderedMessages.length - 1; i >= 0; i--) {
          const msgHeight =
            chatMessageHeights.current?.[renderedMessages[i].id] ??
            MIN_MSG_HEIGHT;
          bottom -= msgHeight;
          if (bottom < scrollBottom && typeof end === 'undefined') {
            end = i;
          }
          if (bottom <= scrollTop && typeof start === 'undefined') {
            start = i;
          }
          // Once we found the boundaries we can stop the loop.
          if (typeof end !== 'undefined' && typeof start !== 'undefined') break;
        }
      } else {
        // No scrolling: render all messages.
        start = 0;
        end = renderedMessages.length - 1;
      }
      if (scrollTop < 10) start = 0;
      if (scrollTop > target.scrollHeight - target.clientHeight - 10)
        end = renderedMessages.length - 1;
      const buffer = Math.ceil(target.clientHeight / MIN_MSG_HEIGHT / 2);
      requestAnimationFrame(() => {
        setViewFrame([
          Math.max(0, (start ?? 0) - buffer),
          Math.max(0, Math.min(end + buffer, renderedMessages.length - 1)),
        ]);
        if (scrollTop < target.scrollHeight - target.clientHeight) {
          scrollToBottomRef?.current?.classList.add(scrollBtnVisibleClass);
        } else {
          scrollToBottomRef?.current?.classList.remove(scrollBtnVisibleClass);
        }
      });
    },
    [MIN_MSG_HEIGHT, enableVirtualScrolling, renderedMessages]
  );

  /**
   * Update scroll frame, after messages were rendered.
   */
  useEffect(() => {
    updateScrollFrame(chatRef.current);
  }, [renderedMessages, updateScrollFrame]);

  /**
   * Trigger recalculation of viewFrame in case no messages are rendered.
   */
  useEffect(() => {
    const renderedCount = viewFrame[1] - viewFrame[0];
    if (renderedMessages.length > 0 && renderedCount >= 0) return;
    const timeout = setTimeout(() => {
      setUpdateHeightCount((c) => c + 1);
    }, 100);
    return () => {
      clearTimeout(timeout);
    };
  }, [renderedMessages, viewFrame]);

  /**
   * Scrolls chat to the bottom.
   * Dependent of whether the chat is visible or not,
   * scrolling is done instantly (invisible) or smoothly (visible).
   */
  const scrollToBottom = useCallback(() => {
    if (!chatRef.current || loading) return;
    const timeout = setTimeout(() => {
      const scrollable = chatRef.current;
      if (!scrollable) return;
      lastScrollTime.current = new Date().getTime();
      scrollable.scrollTop = scrollable.scrollHeight;
    }, 100);
    return () => {
      clearTimeout(timeout);
    };
  }, [loading]);

  /**
   * Initially show chat
   */
  useEffect(() => {
    const scrollable = chatRef.current;
    if (!scrollable || loading || isVisible) return;
    const interval = setInterval(() => {
      // Keep scrolling to bottom until we eventually reach the bottom.
      if (
        scrollable.scrollTop >=
        Math.max(0, scrollable.scrollHeight - scrollable.clientHeight - 10)
      ) {
        setIsVisible(true);
        markMessagesAsRead();
        return;
      }
      scrollable.scrollTop = scrollable.scrollHeight;
    }, 250);

    return () => {
      clearInterval(interval);
      markMessagesAsRead();
    };
  }, [
    isVisible,
    loading,
    markMessagesAsRead,
    scrollToBottom,
    updateHeightCount,
    viewFrame,
  ]);

  /**
   * Automatically scroll to bottom.
   */
  useLayoutEffect(() => {
    const lastMessage = renderedMessages?.[renderedMessages.length - 1];
    // Last new message was sent from local
    if (
      renderedMessages.length > previousRenderedMessages?.length &&
      lastMessage?.local
    ) {
      scrollToBottom();
    }

    // First reaction was added to any message while scrolled down
    const prevMessagesWithReactions = filterMessagesWithReactions(
      previousRenderedMessages ?? []
    );

    const messagesWithReactions = filterMessagesWithReactions(renderedMessages);

    if (
      previousRenderedMessages?.length === renderedMessages.length &&
      prevMessagesWithReactions.length !== messagesWithReactions.length &&
      isScrolledToBottom.current
    ) {
      scrollToBottom();
    }
    // Chat was scrolled to bottom previously -> continue scrolling to bottom
    if (
      renderedMessages.length > previousRenderedMessages?.length &&
      isScrolledToBottom.current
    ) {
      scrollToBottom();
    }

    // Have less than 500ms passed since the last scroll event?
    // If yes, we'll assume we are still scrolling.
    const isScrolling =
      lastScrollTime.current &&
      new Date().getTime() < lastScrollTime.current + 500;
    if (isScrolling) {
      scrollToBottom();
    }
  }, [previousRenderedMessages, renderedMessages, scrollToBottom]);

  const chatMessageRenderTimeout =
    useRef<ReturnType<typeof requestAnimationFrame>>(null);
  const updatedMessageHeights = useRef<Record<string, number>>({});
  const handleMessageRender = useCallback(
    (id: string, el: HTMLElement) => {
      const oldMsg = messages.find((msg) => msg.id === id);
      if (!oldMsg || !el) return;
      cancelAnimationFrame(chatMessageRenderTimeout.current);
      updatedMessageHeights.current[id] = el.clientHeight;
      chatMessageRenderTimeout.current = requestAnimationFrame(() => {
        updateMessageHeights(updatedMessageHeights.current);
        updatedMessageHeights.current = {};
      });
      /**
       * In case the last message reports an updated height (e.g. when a GIF loaded),
       * scroll to the bottom.
       */
      if (
        id === messages[messages.length - 1].id &&
        el.clientHeight > chatMessageHeights.current[id] &&
        isScrolledToBottom.current
      )
        scrollToBottom();
    },
    [messages, scrollToBottom, updateMessageHeights]
  );

  const wrappedChatMessages = useMemo(() => {
    let lastRenderedTime: number = 0;
    return renderedMessages
      .map((msg, i) => {
        const { fromId, received } = msg;
        const prevMsg = renderedMessages?.[i - 1];
        const isNew5MinChunk =
          lastRenderedTime < received.getTime() - 5 * 60 * 1000;
        if (isNew5MinChunk) {
          lastRenderedTime = received.getTime();
        }
        const isDifferentSender = prevMsg?.fromId !== fromId;
        const showBroadcast =
          showBroadcastMessages &&
          msg.room === BROADCAST_CHAT_MESSAGE &&
          (isNew5MinChunk || prevMsg?.room !== BROADCAST_CHAT_MESSAGE);
        const showPrivate =
          showBroadcastMessages &&
          msg.room !== BROADCAST_CHAT_MESSAGE &&
          (isNew5MinChunk || prevMsg?.room === BROADCAST_CHAT_MESSAGE);
        return {
          ...msg,
          showBroadcast,
          showPrivate,
          showSender: isDifferentSender || isNew5MinChunk,
          showTimestamp: isNew5MinChunk,
        };
      })
      .slice(viewFrame[0], viewFrame[1] + 1)
      .map((msg) => {
        const { id, local } = msg;
        return (
          <div
            key={id}
            className={classNames('message-wrapper', { local })}
            data-msg-id={id}
          >
            <ChatMessage
              enableAdvancedChat={enableAdvancedChat}
              message={msg}
              isLocal={local}
              onRender={handleMessageRender}
              showBroadcast={msg.showBroadcast}
              showPrivate={msg.showPrivate}
              showSender={msg.showSender}
              showTimestamp={msg.showTimestamp}
            />
          </div>
        );
      });
  }, [
    enableAdvancedChat,
    handleMessageRender,
    renderedMessages,
    showBroadcastMessages,
    viewFrame,
  ]);

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (ev) => {
    updateScrollFrame(ev.currentTarget);
  };

  const handleScrollToBottomClick = () => {
    scrollToBottom();
    scrollToBottomRef.current.classList.remove(scrollBtnVisibleClass);
  };

  return (
    <div
      className={classNames('messages', {
        virtual: enableVirtualScrolling,
        visible: isVisible,
      })}
      onScroll={enableVirtualScrolling ? handleScroll : null}
      ref={chatRef}
    >
      <div className="messages-inner" ref={innerRef}>
        {wrappedChatMessages}
      </div>
      <Button
        id="chat-scroll-to-bottom"
        ref={scrollToBottomRef}
        className={classNames('scroll-to-bottom', {
          visible:
            Math.ceil(chatRef.current?.scrollTop ?? 0) <
            chatRef.current?.scrollHeight - chatRef.current?.clientHeight,
        })}
        variant="secondary"
        onClick={handleScrollToBottomClick}
      >
        <CaretDownIcon size={16} />
        <VisuallyHidden>{t('general.scrollToBottom')}</VisuallyHidden>
      </Button>
      <style jsx>{`
        .messages {
          flex-grow: 1;
          isolation: isolate;
          opacity: 0;
          overflow-x: hidden;
          overflow-y: auto;
          position: relative;
          scrollbar-color: ${isDarkMode ? 'dark' : 'light'};
          scroll-behavior: auto;
          transition: opacity 250ms ease;
        }
        .messages.visible {
          opacity: 1;
          scroll-behavior: smooth;
        }
        .messages-inner {
          min-height: 100%;
          padding: 0 var(--padding);
          position: relative;
        }
        .messages:not(.virtual) .messages-inner {
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
        }
        .virtual :global(.message-wrapper) {
          left: var(--padding);
          position: absolute;
          top: var(--padding);
          width: calc(100% - 2 * var(--padding));
        }
        .messages :global(.scroll-to-bottom) {
          background: ${colors.backgroundAccent};
          bottom: 8px;
          box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.04),
            0px 0px 4px rgba(0, 0, 0, 0.08);
          float: right;
          margin-right: 4px;
          margin-top: -34px;
          opacity: 0;
          padding: 8px 12px;
          pointer-events: none;
          position: sticky;
          transition: opacity 250ms ease 0ms;
          z-index: ${zIndex.tooltip};
        }
        .messages :global(.scroll-to-bottom.${scrollBtnVisibleClass}) {
          opacity: 1;
          pointer-events: all;
          /* Transition delay to avoid showing the button while chat is scrolling down. */
          transition-delay: 500ms;
        }
      `}</style>
    </div>
  );
};
